// src/utils/dateTimeUtils.js

/**
 * Convert UTC date to IST (Indian Standard Time)
 * IST = UTC + 5:30
 */

/**
 * Format a UTC date string to IST in 12-hour format
 * @param {string} dateString - UTC date string
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date in IST
 */
export const formatUTCToIST = (dateString, options = {}) => {
    if (!dateString) return '-';
    
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        
        // Convert to IST by adding 5:30 hours
        const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
        
        // Default options
        const defaultOptions = {
            showDate: true,
            showTime: true,
            showSeconds: true,
            dateFormat: 'DD/MM/YYYY', // DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD
            timeFormat: '12hr', // 12hr or 24hr
        };
        
        const config = { ...defaultOptions, ...options };
        
        // Extract date parts
        const day = String(istDate.getDate()).padStart(2, '0');
        const month = String(istDate.getMonth() + 1).padStart(2, '0');
        const year = istDate.getFullYear();
        
        // Format date
        let formattedDate = '';
        if (config.showDate) {
            switch (config.dateFormat) {
                case 'DD/MM/YYYY':
                    formattedDate = `${day}/${month}/${year}`;
                    break;
                case 'MM/DD/YYYY':
                    formattedDate = `${month}/${day}/${year}`;
                    break;
                case 'YYYY-MM-DD':
                    formattedDate = `${year}-${month}-${day}`;
                    break;
                default:
                    formattedDate = `${day}/${month}/${year}`;
            }
        }
        
        // Format time
        let formattedTime = '';
        if (config.showTime) {
            let hours = istDate.getHours();
            const minutes = String(istDate.getMinutes()).padStart(2, '0');
            const seconds = String(istDate.getSeconds()).padStart(2, '0');
            
            if (config.timeFormat === '12hr') {
                const ampm = hours >= 12 ? 'PM' : 'AM';
                hours = hours % 12 || 12;
                formattedTime = `${String(hours).padStart(2, '0')}:${minutes}`;
                if (config.showSeconds) {
                    formattedTime += `:${seconds}`;
                }
                formattedTime += ` ${ampm}`;
            } else {
                formattedTime = `${String(hours).padStart(2, '0')}:${minutes}`;
                if (config.showSeconds) {
                    formattedTime += `:${seconds}`;
                }
            }
        }
        
        // Combine date and time
        if (formattedDate && formattedTime) {
            return `${formattedDate} ${formattedTime}`;
        } else if (formattedDate) {
            return formattedDate;
        } else if (formattedTime) {
            return formattedTime;
        }
        
        return '-';
    } catch (error) {
        console.error('Error formatting date:', error);
        return '-';
    }
};

/**
 * Get only IST date from UTC
 */
export const getISTDate = (dateString, format = 'DD/MM/YYYY') => {
    return formatUTCToIST(dateString, { 
        showDate: true, 
        showTime: false,
        dateFormat: format 
    });
};

/**
 * Get only IST time from UTC
 */
export const getISTTime = (dateString, timeFormat = '12hr') => {
    return formatUTCToIST(dateString, { 
        showDate: false, 
        showTime: true,
        timeFormat: timeFormat,
        showSeconds: true
    });
};

/**
 * Get IST date and time in 12-hour format
 */
export const getISTDateTime12hr = (dateString) => {
    return formatUTCToIST(dateString, {
        showDate: true,
        showTime: true,
        timeFormat: '12hr',
        showSeconds: true,
        dateFormat: 'DD/MM/YYYY'
    });
};

/**
 * Get IST date and time in 24-hour format
 */
export const getISTDateTime24hr = (dateString) => {
    return formatUTCToIST(dateString, {
        showDate: true,
        showTime: true,
        timeFormat: '24hr',
        showSeconds: true,
        dateFormat: 'DD/MM/YYYY'
    });
};

/**
 * Convert UTC to IST Date object
 */
export const convertToISTDate = (dateString) => {
    if (!dateString) return null;
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return null;
        return new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    } catch (error) {
        console.error('Error converting to IST:', error);
        return null;
    }
};

/**
 * Check if a date is today (IST)
 */
export const isTodayIST = (dateString) => {
    const istDate = convertToISTDate(dateString);
    if (!istDate) return false;
    const today = new Date();
    const todayIST = new Date(today.getTime() + (5.5 * 60 * 60 * 1000));
    return istDate.getDate() === todayIST.getDate() &&
           istDate.getMonth() === todayIST.getMonth() &&
           istDate.getFullYear() === todayIST.getFullYear();
};

/**
 * Get relative time (e.g., "2 hours ago", "5 minutes ago")
 */
export const getRelativeTimeIST = (dateString) => {
    const istDate = convertToISTDate(dateString);
    if (!istDate) return '-';
    
    const now = new Date();
    const nowIST = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
    const diffMs = nowIST - istDate;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
    if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
    if (diffDay < 30) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
    if (diffDay < 365) return `${Math.floor(diffDay / 30)} month${Math.floor(diffDay / 30) > 1 ? 's' : ''} ago`;
    return `${Math.floor(diffDay / 365)} year${Math.floor(diffDay / 365) > 1 ? 's' : ''} ago`;
};

// Default export with all functions
export default {
    formatUTCToIST,
    getISTDate,
    getISTTime,
    getISTDateTime12hr,
    getISTDateTime24hr,
    convertToISTDate,
    isTodayIST,
    getRelativeTimeIST
};